'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { VariantInformationForm, VariantPricesFormSection } from '@/components/form/variant';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { ErrorDisplay } from '@/components/ui/error-display';
import { useCreateVariant } from '@/lib/api/variants';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { variantSchema, type VariantFormData } from '../validation';

const CreateVariantPageInner = () => {
  const { productId } = useParams<{ id: string; productId: string }>();
  const { storeRoutes, navigate, id: storeId } = useStoreNavigate();
  const { t } = useTranslation('products');
  const { toast } = useToast();

  if (!productId) {
    throw new Error('Store ID and Product ID are required');
  }

  const createVariantMutation = useCreateVariant();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

  const methods = useForm<VariantFormData>({
    defaultValues: {
      name: '',
      description: '',
      sku: null,
      imageUrl: null,
      grams: null,
      active: true,
      default: false,
      prices: [],
      compareAtPrice: null,
      price: null,
    },
    resolver: zodResolver(variantSchema),
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleCreateVariant = async (data: VariantFormData) => {
    try {
      setFormError(null);

      await createVariantMutation.mutateAsync({
        productId,
        data,
      });

      toast({
        title: t('variantCreated'),
        description: t('variantCreatedSuccess'),
      });
      navigate(storeRoutes.PRODUCT_DETAILS(productId));
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);

      if (!parsedError.isValidationError) {
        toast({
          title: t('createError'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  const handleCancel = () => {
    navigate(storeRoutes.PRODUCT_DETAILS(productId));
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleCreateVariant)}>
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-4">
                <BackButton to={storeRoutes.PRODUCT_DETAILS(productId)} />
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">
                    {t('createVariant')}
                  </h1>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('createVariantDescription')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button type="submit" size="sm" disabled={isSubmitting || createVariantMutation.isPending}>
                  {isSubmitting || createVariantMutation.isPending
                    ? t('creating')
                    : t('createVariant')}
                </Button>
              </div>
            </div>

            {/* Error Display */}
            {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

            {/* Main Content */}
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* Main content - 1/2 width */}
              <div className="space-y-6">
                <VariantInformationForm storeId={storeId} />
              </div>

              {/* Sidebar - 1/2 width */}
              <div className="space-y-6">
                <VariantPricesFormSection storeId={storeId} />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export const CreateVariantPage = withRequiredPermission(CreateVariantPageInner, PERMISSIONS.STORES_MANAGE);
export default CreateVariantPage;
